import Communications from '../../../../../_helpers/InterComponentCommunications';

export interface OktaConfig {
	clientId: string;
	issuer: string;
	redirectUri: string;
	signInUrl: string;
	scope: string;
}

export const chatblk_access_token = 'chatblk_access_token';

export const chatblk_config = 'chatblk_config';

export class OktaAuthService {
	private static generateRandomString(length: number): string {
		const chars =
			'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		let randomString = '';
		for (let i = 0; i < length; i++) {
			randomString += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		return randomString;
	}

	private static async generateCodeChallenge(
		codeVerifier: string
	): Promise<string> {
		const encoder = new TextEncoder();
		const data = encoder.encode(codeVerifier);
		const digest = await crypto.subtle.digest('SHA-256', data);
		return OktaAuthService.bufferToBase64Url(digest);
	}

	private static bufferToBase64Url(buffer: ArrayBuffer): string {
		const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
		return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
	}

	public static async initiateLogin(userEmail: string, config: OktaConfig) {
		const codeVerifier = this.generateRandomString(128);
		const codeChallenge = await this.generateCodeChallenge(codeVerifier);

		// Store the codeVerifier temporarily in localStorage for the callback
		localStorage.setItem('chatblk_codeVerifier', codeVerifier);

		// Set redirectUri to the current URL
		const redirectUri = window.location.origin + window.location.pathname;

		// Construct the authorization URL
		const authorizationUrl = `${config.issuer}/v1/authorize?client_id=${config.clientId}&response_type=code&scope=${config.scope}&redirect_uri=${redirectUri}&state=${this.generateRandomString(16)}&code_challenge=${codeChallenge}&code_challenge_method=S256&login_hint=${userEmail}`;

		// Store the chatblk_config object in localStorage
		const chatblkConfigObject = {
			chatblk_issuer: new URL(config.issuer).origin, // Extract only the protocol + domain + optional port
			chatblk_signin_url: config.signInUrl,
		};
		localStorage.setItem(chatblk_config, JSON.stringify(chatblkConfigObject));

		Communications.advisorCenter.chatBlkConfigLocalStorageSet.dispatch(
			chatblkConfigObject.chatblk_issuer
		);

		// Redirect the user to the authorization endpoint
		window.location.href = authorizationUrl;
	}

	public static async handleCallback(config: OktaConfig) {
		const urlParams = new URLSearchParams(window.location.search);
		const authorizationCode = urlParams.get('code');
		const state = urlParams.get('state');

		if (!authorizationCode || !state) {
			console.log('No authorization code or state found in URL');
			return;
		}

		const codeVerifier = localStorage.getItem('chatblk_codeVerifier');

		if (!codeVerifier) {
			console.error('No code verifier found in session storage');
			return;
		}

		try {
			// Use the current page's full URL as the redirect_uri
			const redirectUri = window.location.origin + window.location.pathname;

			// Exchange the authorization code for tokens
			const tokenResponse = await fetch(`${config.issuer}/v1/token`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: new URLSearchParams({
					grant_type: 'authorization_code',
					client_id: config.clientId,
					code: authorizationCode,
					redirect_uri: redirectUri,
					code_verifier: codeVerifier,
				}),
			});

			if (!tokenResponse.ok) {
				console.error(
					`Token request failed with status: ${tokenResponse.status}`
				);
				const errorText = await tokenResponse.text();
				console.error(`Error details: ${errorText}`);
				throw new Error(`Token request failed: ${tokenResponse.status}`);
			}

			const tokens = await tokenResponse.json();

			if (tokens.access_token) {
				const accessTokenData = {
					token_type: tokens.token_type,
					expires_in: tokens.expires_in,
					access_token: tokens.access_token,
					scope: tokens.scope,
					created_at: Date.now(),
				};

				// Store the access token in sessionStorage
				sessionStorage.setItem(
					chatblk_access_token,
					JSON.stringify(accessTokenData)
				);

				// Remove the codeVerifier after use
				localStorage.removeItem('chatblk_codeVerifier');
			} else {
				console.error(
					'Failed to get access token - no token in response:',
					tokens
				);
			}
		} catch (error) {
			console.error('Error exchanging code for tokens:', error);
		}
	}

	public static async initialize(config: OktaConfig) {
		const urlParams = new URLSearchParams(window.location.search);
		const authorizationCode = urlParams.get('code');
		const codeVerifier = localStorage.getItem('chatblk_codeVerifier');

		// If there is a code in the URL and a codeVerifier in session storage, handle the callback
		if (authorizationCode && codeVerifier) {
			await this.handleCallback(config);
		}
	}
}
